import { formatPublicSourceLabel } from "@/features/machines/addressing";
import type { GhmSignalKind } from "@ghm/mqtt-contract";

/**
 * Como um sinal se chama. UMA resposta, uma precedencia.
 *
 * Havia tres fontes disputando o nome de um sinal, e qual vencia dependia do
 * caminho no codigo: o rotulo publicado no `/meta`, o `SignalConfig.label`
 * gravado a partir do titulo de um card, e o rotulo tecnico derivado da tag.
 * O mesmo sinal aparecia como "Porta do painel — Transicoes" no seletor,
 * "Producao atual - Sinal" no card e "Entrada 1 - Total" na legenda — tres
 * nomes para uma coisa so, e o unico verdadeiro (o do dispositivo) era o que
 * nao aparecia.
 *
 * A ordem e:
 *
 *  1. O que o OPERADOR escolheu. E deliberado e vence tudo — mas so conta como
 *     escolha quando foi digitado; ver `WidgetSettings.renamedByOperator` e
 *     `SignalConfig.labelFromOperator`.
 *  2. O que o DISPOSITIVO declara agora. E a fonte da verdade sobre o que a
 *     coisa e, e muda quando o configurador muda.
 *  3. O nome SALVO no card. Pode ser um default velho, mas e um nome que
 *     alguem ja viu na tela — vale mais que um generico enquanto o device
 *     esta mudo.
 *  4. O rotulo TECNICO da tag ("Entrada 5 - Leitura"). Nao descreve a
 *     grandeza, so localiza a porta. Ultimo recurso.
 *
 * Os campos chegam como strings ja resolvidas, e nao como `SignalConfig`, de
 * proposito: a provenencia mora em DOIS lugares (a marca no widget e a marca
 * na config compartilhada), entao quem chama precisa decidir o que conta como
 * escolha antes de perguntar o nome. Receber o objeto convidava cada chamador
 * a repetir essa decisao — foi assim que a precedencia acabou reimplementada
 * inline no `SignalCard` enquanto esta funcao ficava sem nenhum chamador.
 */
export function resolveSignalName(
  source: string,
  options: {
    /** Nome que o operador DIGITOU, se houve. */
    chosen?: string;
    /** Nome publicado no `/meta` para esta fonte, se ela ainda existe la. */
    declared?: string;
    /** Nome que ficou SALVO no card. Pode ser um default antigo. */
    saved?: string;
    /** Semantica declarada, para o rotulo tecnico nao chamar tudo de "Velocidade". */
    kind?: GhmSignalKind;
  } = {},
): string {
  const chosen = options.chosen?.trim();
  if (chosen) return chosen;

  const declared = options.declared?.trim();
  if (declared) return declared;

  // O nome salvo vem ANTES do rotulo tecnico mesmo podendo ser um default
  // velho: com o gateway fora do ar `declared` some, e cair direto no tecnico
  // trocaria TODO titulo do painel por "Entrada N - Tipo" durante a queda.
  const saved = options.saved?.trim();
  if (saved) return saved;

  return formatPublicSourceLabel(source, options.kind);
}

/**
 * Junta o rotulo da FUNCAO com o do VALOR ("Vazao da linha — Vazao").
 *
 * Quando os dois coincidem, devolve um so. O configurador nao impede batizar a
 * funcao e o valor com a mesma palavra, e ai o nome saia duplicado — o painel
 * exibia literalmente "Tensao L1 — Tensao L1".
 */
export function joinFunctionAndValueLabel(
  functionLabel: string,
  valueLabel: string,
): string {
  const fn = functionLabel.trim();
  const value = valueLabel.trim();
  if (!value || fn.toLocaleLowerCase() === value.toLocaleLowerCase()) return fn;
  if (!fn) return value;
  return `${fn} — ${value}`;
}
